import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseResourceComponent } from '@memberjunction/ng-shared';
import { Metadata, RunView } from '@memberjunction/core';
import { BCBaseComponent } from '@bluecypress/bcsaas-ng-core';
import { BCTenantContext } from '@bluecypress/bcsaas-core';
import { marked } from 'marked';

interface NoteItem {
  ID: string;
  OrganizationID: string;
  Phase: string;
  Source: string;
  Status: string;
  Priority: number;
  Note: string;
  UpdatedAt: Date | null;
  CreatedAt: Date | null;
}

interface PhaseInfo {
  value: string;
  display: string;
  cssClass: string;
}

@Component({
  standalone: false,
  selector: 'app-notes-resource',
  template: `
    <div class="notes-app">

      <!-- ── SIDEBAR ── -->
      <aside class="sidebar">
        <div class="sidebar__header">
          <div class="sidebar__title-row">
            <div>
              <span class="sidebar__title">Notes</span>
              <span class="sidebar__count">{{ FilteredNotes.length }}</span>
            </div>
            <button class="btn-new-note" (click)="EnterCreateMode()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 5v14M5 12h14"/></svg>
              New
            </button>
          </div>
          <div class="sidebar__search">
            <svg class="sidebar__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input class="sidebar__search-input" type="text" placeholder="Search notes..."
                   [(ngModel)]="SearchTerm" (input)="FilterNotes()" />
          </div>
        </div>

        <!-- Org filter -->
        <div class="sidebar__org-filter" *ngIf="Organizations.length > 1">
          <svg class="sidebar__org-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <select class="sidebar__org-select" [ngModel]="CurrentOrgID" (ngModelChange)="OnOrgFilterChange($event)">
            <option *ngFor="let org of Organizations" [value]="org.ID">{{ org.Name }}</option>
          </select>
        </div>

        <!-- Filters -->
        <div class="sidebar__filters">
          <button class="filter-chip" [class.active]="StatusFilter === 'All'" (click)="SetStatusFilter('All')">All</button>
          <button class="filter-chip" [class.active]="StatusFilter === 'Approved'" (click)="SetStatusFilter('Approved')">Approved</button>
          <button class="filter-chip" [class.active]="StatusFilter === 'Pending'" (click)="SetStatusFilter('Pending')">Pending</button>
          <button class="filter-chip" [class.active]="StatusFilter === 'Rejected'" (click)="SetStatusFilter('Rejected')">Rejected</button>
        </div>

        <!-- Note list -->
        <div class="sidebar__list">

          <!-- Loading skeletons -->
          <ng-container *ngIf="Loading">
            <div class="skeleton-card" *ngFor="let i of [1,2,3,4]">
              <div class="skeleton-line w-40"></div>
              <div class="skeleton-line w-80"></div>
              <div class="skeleton-line w-60"></div>
              <div class="skeleton-line w-30"></div>
            </div>
          </ng-container>

          <!-- Draft card when creating -->
          <div class="note-card draft" *ngIf="CreateMode && !Loading">
            <div class="note-card__tags">
              <span class="note-card__draft-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                New Draft
              </span>
              <ng-container *ngFor="let p of GetPhaseList(NewNote.Phase)">
                <span class="phase-tag" [ngClass]="GetPhaseCssClass(p)">{{ GetPhaseDisplayName(p) }}</span>
              </ng-container>
            </div>
            <p class="note-card__preview">{{ NewNote.Note || 'Start writing your note...' }}</p>
            <div class="note-card__footer">
              <span class="note-card__source" style="color: var(--accent);">Just now</span>
            </div>
          </div>

          <!-- Empty state -->
          <div class="sidebar__empty" *ngIf="!Loading && FilteredNotes.length === 0 && !CreateMode">
            <svg class="sidebar__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
            <p>No notes found</p>
          </div>

          <!-- Note cards -->
          <ng-container *ngIf="!Loading">
            <div class="note-card"
                 *ngFor="let note of FilteredNotes"
                 [class.active]="SelectedNote?.ID === note.ID && !CreateMode"
                 (click)="SelectNote(note)">
              <div class="note-card__tags">
                <ng-container *ngFor="let p of GetPhaseList(note.Phase)">
                  <span class="phase-tag" [ngClass]="GetPhaseCssClass(p)">{{ GetPhaseDisplayName(p) }}</span>
                </ng-container>
                <span class="status-dot" [ngClass]="note.Status.toLowerCase()">{{ note.Status }}</span>
              </div>
              <p class="note-card__preview">{{ GetPreview(note.Note) }}</p>
              <div class="note-card__footer">
                <span class="note-card__source">
                  <svg *ngIf="note.Source === 'Manual'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                  <svg *ngIf="note.Source !== 'Manual'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  {{ note.Source }}
                </span>
                <span class="note-card__date" *ngIf="note.UpdatedAt">{{ note.UpdatedAt | date:'MMM d' }}</span>
              </div>
            </div>
          </ng-container>

        </div>
      </aside>

      <!-- ── EDITOR: CREATE MODE ── -->
      <main class="editor" *ngIf="CreateMode">
        <div class="create-banner">
          <div class="create-banner__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
          </div>
          <div class="create-banner__text">
            <div class="create-banner__title">New Note</div>
            <div class="create-banner__hint">Fill in the details below and start writing</div>
          </div>
          <div class="create-banner__actions">
            <button class="btn-action btn-secondary" (click)="CancelCreate()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
              Cancel
            </button>
            <button class="btn-action btn-primary" [disabled]="CreatingNote" (click)="CreateNote()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
              {{ CreatingNote ? 'Creating...' : 'Create Note' }}
            </button>
          </div>
        </div>

        <!-- Meta row 1: Status, Priority -->
        <div class="editor__meta">
          <div class="meta-item">
            <span class="meta-item__label">Status</span>
            <div class="meta-item__value">
              <select class="meta-select" [(ngModel)]="NewNote.Status">
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div class="meta-item">
            <span class="meta-item__label">Priority</span>
            <div class="meta-item__value">
              <div class="meta-priority">
                <div *ngFor="let i of PriorityBars"
                     class="meta-priority__bar"
                     [class.filled]="i <= (10 - NewNote.Priority)"
                     (click)="SetNewNotePriority(i)"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Meta row 2: Phase pills -->
        <div class="editor__meta">
          <div class="meta-item" style="border-right: none;">
            <span class="meta-item__label">Phase</span>
            <div class="meta-item__value" style="gap: 5px;">
              <span *ngFor="let p of PhaseMap"
                    class="meta-phase-pill"
                    [ngClass]="p.cssClass"
                    [class.active]="IsNewNotePhaseSelected(p.value)"
                    (click)="ToggleNewNotePhase(p.value)">
                <svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                {{ p.display }}
              </span>
            </div>
          </div>
        </div>

        <!-- Editor body -->
        <div class="editor__body">
          <div class="editor__writing-area">
            <textarea class="editor__textarea"
                      spellcheck="false"
                      placeholder="Start writing your note in markdown..."
                      [(ngModel)]="NewNote.Note"></textarea>
          </div>
        </div>

        <!-- Footer -->
        <div class="editor__footer">
          <div class="editor__footer-left">
            <span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              {{ NewNote.Note.length }} characters
            </span>
            <span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Markdown
            </span>
          </div>
          <span>Draft &mdash; not yet saved</span>
        </div>
      </main>

      <!-- ── EDITOR: EDIT MODE ── -->
      <main class="editor" *ngIf="!CreateMode && SelectedNote">
        <!-- Toolbar -->
        <div class="editor__toolbar">
          <div class="editor__toolbar-left">
            <div class="segmented-control">
              <button class="segmented-control__btn" [class.active]="!PreviewMode" (click)="SetPreviewMode(false)">Edit</button>
              <button class="segmented-control__btn" [class.active]="PreviewMode" (click)="SetPreviewMode(true)">Preview</button>
            </div>
            <span class="unsaved-dot" *ngIf="Dirty"></span>
          </div>
          <div class="editor__toolbar-right">
            <button class="btn-action btn-danger" [disabled]="Saving || Deleting" (click)="DeleteNote()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
              {{ Deleting ? 'Deleting...' : 'Delete' }}
            </button>
            <button class="btn-action btn-secondary" [disabled]="!Dirty" (click)="DiscardChanges()">Discard</button>
            <button class="btn-action btn-primary" [disabled]="!Dirty || Saving" (click)="SaveNote()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
              {{ Saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
        </div>

        <!-- Meta bar -->
        <div class="editor__meta">
          <div class="meta-item" style="border-right: none; flex: 1;">
            <span class="meta-item__label">Phase</span>
            <div class="meta-item__value" style="gap: 5px;">
              <span *ngFor="let p of PhaseMap"
                    class="meta-phase-pill"
                    [ngClass]="p.cssClass"
                    [class.active]="IsPhaseSelected(p.value)"
                    (click)="TogglePhase(p.value)">
                <svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                {{ p.display }}
              </span>
            </div>
          </div>
        </div>
        <div class="editor__meta">
          <div class="meta-item">
            <span class="meta-item__label">Status</span>
            <div class="meta-item__value">
              <select class="meta-select" [(ngModel)]="SelectedNote.Status" (change)="OnStatusChange()">
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div class="meta-item">
            <span class="meta-item__label">Priority</span>
            <div class="meta-item__value">
              <div class="meta-priority">
                <div *ngFor="let i of PriorityBars"
                     class="meta-priority__bar"
                     [class.filled]="i <= (10 - SelectedNote.Priority)"
                     (click)="SetPriority(i)"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Status change reason -->
        <div class="editor__meta editor__meta--reason" *ngIf="StatusChangeReason !== null">
          <div class="meta-item" style="border-right: none; flex: 1;">
            <span class="meta-item__label">Reason for Change</span>
            <div class="meta-item__value" style="flex: 1;">
              <input type="text" class="meta-reason-input"
                     [(ngModel)]="StatusChangeReason"
                     (input)="MarkDirty()"
                     placeholder="Why is this status changing?" />
            </div>
          </div>
        </div>

        <!-- Editor body -->
        <div class="editor__body">
          <div class="editor__writing-area">
            <textarea *ngIf="!PreviewMode"
                      class="editor__textarea"
                      spellcheck="false"
                      placeholder="Write your note in markdown..."
                      [(ngModel)]="SelectedNote.Note"
                      (input)="MarkDirty()"></textarea>
            <div *ngIf="PreviewMode"
                 class="editor__preview"
                 [innerHTML]="RenderedMarkdown"></div>
          </div>
        </div>

        <!-- Footer -->
        <div class="editor__footer">
          <div class="editor__footer-left">
            <span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              {{ SelectedNote.Note.length }} characters
            </span>
            <span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Markdown
            </span>
          </div>
          <span *ngIf="SelectedNote.UpdatedAt">Last saved {{ SelectedNote.UpdatedAt | date:'MMM d, y' }} at {{ SelectedNote.UpdatedAt | date:'h:mm a' }}</span>
        </div>
      </main>

      <!-- ── EMPTY STATE ── -->
      <main class="empty-state" *ngIf="!CreateMode && !SelectedNote">
        <div class="empty-state__illustration">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
        </div>
        <div class="empty-state__title">Select a note</div>
        <div class="empty-state__desc">Choose a note from the sidebar to view and edit, or create a new one.</div>
        <button class="btn-action btn-primary" (click)="EnterCreateMode()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
          Create New Note
        </button>
      </main>

      <!-- Dialog Overlay -->
      <div class="dialog-overlay" *ngIf="DialogVisible" (click)="OnDialogClose(false)">
        <div class="dialog-box" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <span class="dialog-title">{{ DialogTitle }}</span>
            <button class="dialog-close-btn" (click)="OnDialogClose(false)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="dialog-body">{{ DialogMessage }}</div>
          <div class="dialog-footer">
            <ng-container *ngIf="DialogType === 'confirm'">
              <button class="btn-action btn-secondary" (click)="OnDialogClose(false)">Cancel</button>
              <button class="btn-action btn-danger" (click)="OnDialogClose(true)">Confirm</button>
            </ng-container>
            <button *ngIf="DialogType === 'alert'" class="btn-action btn-primary" (click)="OnDialogClose(false)">OK</button>
          </div>
        </div>
      </div>

    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
      /* ── Design tokens (mapped to MJ global tokens for dark-mode support) ── */
      --bg-app: var(--mj-bg-page);
      --bg-surface: var(--mj-bg-surface);
      --bg-surface-hover: var(--mj-bg-surface-hover);
      --bg-surface-active: var(--mj-brand-accent-subtle);
      --bg-muted: var(--mj-bg-surface-card);
      --border-subtle: var(--mj-border-subtle);
      --border-default: var(--mj-border-default);
      --border-active: var(--mj-color-violet-400);
      --border-focus: var(--mj-color-indigo-500);
      --text-primary: var(--mj-text-primary);
      --text-secondary: var(--mj-text-secondary);
      --text-tertiary: var(--mj-text-muted);
      --text-inverse: var(--mj-text-inverse);
      --accent: var(--mj-color-indigo-500);
      --accent-hover: var(--mj-color-indigo-600);
      --accent-subtle: var(--mj-color-indigo-50);
      --accent-muted: var(--mj-color-violet-200);
      --status-approved-bg: var(--mj-status-success-bg);
      --status-approved-text: var(--mj-status-success-text);
      --status-approved-dot: var(--mj-status-success);
      --status-pending-bg: var(--mj-status-warning-bg);
      --status-pending-text: var(--mj-status-warning-text);
      --status-pending-dot: var(--mj-status-warning);
      --status-rejected-bg: var(--mj-status-error-bg);
      --status-rejected-text: var(--mj-status-error-text);
      --status-rejected-dot: var(--mj-status-error);
      --phase-purple-bg: var(--mj-color-violet-50);
      --phase-purple-text: var(--mj-color-violet-700);
      --phase-blue-bg: var(--mj-color-info-50);
      --phase-blue-text: var(--mj-color-info-700);
      --phase-teal-bg: var(--mj-color-tertiary-50);
      --phase-teal-text: var(--mj-color-tertiary-700);
      --phase-amber-bg: var(--mj-color-warning-50);
      --phase-amber-text: var(--mj-color-warning-700);
      --phase-rose-bg: var(--mj-color-error-50);
      --phase-rose-text: var(--mj-color-error-700);
      --radius-sm: var(--mj-radius-sm);
      --radius-md: var(--mj-radius-md);
      --radius-lg: var(--mj-radius-lg);
      --radius-full: var(--mj-radius-full);
      --shadow-xs: var(--mj-shadow-sm);
      --shadow-sm: var(--mj-shadow-sm);
      --ease: cubic-bezier(0.25, 0.1, 0.25, 1);
      --duration: 180ms;
    }

    /* ── Layout ── */
    .notes-app {
      display: grid;
      grid-template-columns: 340px 1fr;
      height: 100%;
      overflow: hidden;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      -webkit-font-smoothing: antialiased;
      color: var(--text-primary);
    }

    /* ── Sidebar ── */
    .sidebar {
      background: var(--bg-surface);
      border-right: 1px solid var(--border-subtle);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .sidebar__header {
      padding: 20px 20px 0;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .sidebar__title-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .sidebar__title {
      font-size: 1.25rem;
      font-weight: 700;
      color: var(--text-primary);
      letter-spacing: -0.02em;
    }
    .sidebar__count {
      font-size: 0.8rem;
      font-weight: 500;
      color: var(--text-tertiary);
      background: var(--bg-muted);
      padding: 2px 8px;
      border-radius: var(--radius-full);
      margin-left: 8px;
    }
    .btn-new-note {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: var(--accent);
      color: var(--text-inverse);
      border: none;
      border-radius: var(--radius-md);
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      transition: all var(--duration) var(--ease);
    }
    .btn-new-note:hover {
      background: var(--accent-hover);
      transform: translateY(-1px);
      box-shadow: var(--shadow-sm);
    }
    .btn-new-note:active { transform: translateY(0); }
    .btn-new-note svg { width: 14px; height: 14px; stroke-width: 2.5; }

    /* Search */
    .sidebar__search { position: relative; }
    .sidebar__search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      width: 15px;
      height: 15px;
      color: var(--text-tertiary);
      pointer-events: none;
    }
    .sidebar__search-input {
      width: 100%;
      padding: 9px 12px 9px 36px;
      background: var(--bg-muted);
      border: 1.5px solid transparent;
      border-radius: var(--radius-md);
      font-size: 0.835rem;
      font-family: inherit;
      color: var(--text-primary);
      outline: none;
      transition: all var(--duration) var(--ease);
      box-sizing: border-box;
    }
    .sidebar__search-input::placeholder { color: var(--text-tertiary); }
    .sidebar__search-input:focus {
      background: var(--bg-surface);
      border-color: var(--border-active);
      box-shadow: 0 0 0 3px var(--accent-subtle);
    }

    /* Org filter */
    .sidebar__org-filter {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px 0;
    }
    .sidebar__org-icon {
      width: 14px;
      height: 14px;
      color: var(--text-tertiary);
      flex-shrink: 0;
    }
    .sidebar__org-select {
      flex: 1;
      appearance: none;
      -webkit-appearance: none;
      background: var(--bg-muted);
      border: 1.5px solid transparent;
      border-radius: var(--radius-md);
      padding: 6px 28px 6px 10px;
      font-size: 0.8rem;
      font-weight: 600;
      font-family: inherit;
      color: var(--text-primary);
      cursor: pointer;
      outline: none;
      transition: all var(--duration) var(--ease);
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23a1a1aa' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 10px center;
    }
    .sidebar__org-select:focus {
      border-color: var(--border-active);
      background-color: var(--bg-surface);
      box-shadow: 0 0 0 3px var(--accent-subtle);
    }

    /* Filters */
    .sidebar__filters {
      display: flex;
      gap: 6px;
      padding: 16px 20px 12px;
    }
    .filter-chip {
      padding: 5px 12px;
      border-radius: var(--radius-full);
      border: 1.5px solid var(--border-subtle);
      background: var(--bg-surface);
      font-size: 0.77rem;
      font-weight: 500;
      color: var(--text-secondary);
      cursor: pointer;
      transition: all var(--duration) var(--ease);
      user-select: none;
      font-family: inherit;
    }
    .filter-chip:hover {
      border-color: var(--border-default);
      background: var(--bg-muted);
    }
    .filter-chip.active {
      background: var(--accent-subtle);
      border-color: var(--accent-muted);
      color: var(--accent);
      font-weight: 600;
    }

    /* Note list */
    .sidebar__list {
      flex: 1;
      overflow-y: auto;
      padding: 0 12px 12px;
      scrollbar-width: thin;
      scrollbar-color: var(--border-default) transparent;
    }
    .sidebar__list::-webkit-scrollbar { width: 5px; }
    .sidebar__list::-webkit-scrollbar-track { background: transparent; }
    .sidebar__list::-webkit-scrollbar-thumb {
      background: var(--border-default);
      border-radius: var(--radius-full);
    }

    /* Note card */
    .note-card {
      padding: 14px 16px;
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all var(--duration) var(--ease);
      border: 1.5px solid transparent;
      margin-bottom: 2px;
      position: relative;
    }
    .note-card:hover { background: var(--bg-surface-hover); }
    .note-card.active {
      background: var(--bg-surface-active);
      border-color: var(--accent-muted);
    }
    .note-card.active::before {
      content: '';
      position: absolute;
      left: 0;
      top: 12px;
      bottom: 12px;
      width: 3px;
      border-radius: 0 2px 2px 0;
      background: var(--accent);
    }

    .note-card__tags {
      display: flex;
      gap: 5px;
      flex-wrap: wrap;
      margin-bottom: 8px;
    }
    .phase-tag {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: var(--radius-full);
      font-size: 0.68rem;
      font-weight: 600;
      letter-spacing: 0.01em;
      line-height: 1.6;
    }
    .phase-tag.requirements { background: var(--phase-purple-bg); color: var(--phase-purple-text); }
    .phase-tag.tpm          { background: var(--phase-blue-bg);   color: var(--phase-blue-text); }
    .phase-tag.data         { background: var(--phase-teal-bg);   color: var(--phase-teal-text); }
    .phase-tag.architect    { background: var(--phase-amber-bg);  color: var(--phase-amber-text); }
    .phase-tag.coder        { background: var(--phase-rose-bg);   color: var(--phase-rose-text); }

    .status-dot {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 0.68rem;
      font-weight: 600;
      padding: 2px 8px;
      border-radius: var(--radius-full);
    }
    .status-dot::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }
    .status-dot.approved { background: var(--status-approved-bg); color: var(--status-approved-text); }
    .status-dot.approved::before { background: var(--status-approved-dot); }
    .status-dot.pending { background: var(--status-pending-bg); color: var(--status-pending-text); }
    .status-dot.pending::before { background: var(--status-pending-dot); }
    .status-dot.rejected { background: var(--status-rejected-bg); color: var(--status-rejected-text); }
    .status-dot.rejected::before { background: var(--status-rejected-dot); }

    .note-card__preview {
      font-size: 0.825rem;
      color: var(--text-secondary);
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      margin: 0 0 10px;
    }
    .note-card__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .note-card__source {
      font-size: 0.72rem;
      color: var(--text-tertiary);
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .note-card__source svg { width: 12px; height: 12px; }
    .note-card__date { font-size: 0.72rem; color: var(--text-tertiary); }

    /* Draft card */
    .note-card.draft {
      border: 1.5px dashed var(--accent-muted);
      background: var(--accent-subtle);
      position: relative;
    }
    .note-card.draft::before {
      content: '';
      position: absolute;
      left: 0;
      top: 12px;
      bottom: 12px;
      width: 3px;
      border-radius: 0 2px 2px 0;
      background: var(--accent);
    }
    .note-card__draft-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: var(--radius-full);
      font-size: 0.68rem;
      font-weight: 600;
      background: var(--accent);
      color: var(--text-inverse);
    }
    .note-card__draft-badge svg { width: 10px; height: 10px; }
    .note-card.draft .note-card__preview {
      color: var(--text-tertiary);
      font-style: italic;
    }

    /* Skeleton loading */
    .skeleton-card {
      padding: 14px 16px;
      border-radius: var(--radius-md);
      margin-bottom: 2px;
    }
    .skeleton-line {
      height: 10px;
      border-radius: 4px;
      background: linear-gradient(90deg, var(--bg-muted) 25%, #e8e8ec 50%, var(--bg-muted) 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s ease infinite;
    }
    .skeleton-line.w-40 { width: 40%; }
    .skeleton-line.w-80 { width: 80%; margin-top: 10px; }
    .skeleton-line.w-60 { width: 60%; margin-top: 6px; }
    .skeleton-line.w-30 { width: 30%; margin-top: 12px; }
    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* Empty sidebar */
    .sidebar__empty {
      text-align: center;
      padding: 48px 24px;
      color: var(--text-tertiary);
    }
    .sidebar__empty-icon {
      width: 48px;
      height: 48px;
      margin: 0 auto 12px;
      opacity: 0.35;
    }
    .sidebar__empty p { font-size: 0.85rem; margin: 0; }

    /* ── Editor Pane ── */
    .editor {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: var(--bg-app);
    }

    /* Toolbar */
    .editor__toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 28px;
      background: var(--bg-surface);
      border-bottom: 1px solid var(--border-subtle);
    }
    .editor__toolbar-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .editor__toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Segmented control */
    .segmented-control {
      display: flex;
      background: var(--bg-muted);
      border-radius: var(--radius-md);
      padding: 3px;
      gap: 2px;
    }
    .segmented-control__btn {
      padding: 5px 14px;
      border: none;
      border-radius: 7px;
      font-size: 0.8rem;
      font-weight: 500;
      font-family: inherit;
      color: var(--text-tertiary);
      background: transparent;
      cursor: pointer;
      transition: all var(--duration) var(--ease);
    }
    .segmented-control__btn.active {
      background: var(--bg-surface);
      color: var(--text-primary);
      box-shadow: var(--shadow-xs);
      font-weight: 600;
    }
    .segmented-control__btn:not(.active):hover { color: var(--text-secondary); }

    /* Unsaved dot */
    .unsaved-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--accent);
      display: inline-block;
      animation: pulse 2s ease infinite;
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.4; }
    }

    /* Action buttons */
    .btn-action {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 16px;
      border-radius: var(--radius-md);
      font-size: 0.8rem;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: all var(--duration) var(--ease);
      border: none;
      letter-spacing: -0.01em;
    }
    .btn-action:disabled { opacity: 0.35; cursor: not-allowed; }
    .btn-action svg { width: 14px; height: 14px; }
    .btn-primary { background: var(--accent); color: var(--text-inverse); }
    .btn-primary:not(:disabled):hover {
      background: var(--accent-hover);
      transform: translateY(-1px);
      box-shadow: var(--shadow-sm);
    }
    .btn-primary:active { transform: translateY(0); }
    .btn-secondary {
      background: var(--bg-muted);
      color: var(--text-secondary);
      border: 1.5px solid var(--border-subtle);
    }
    .btn-secondary:not(:disabled):hover {
      background: var(--bg-surface-hover);
      border-color: var(--border-default);
    }
    .btn-danger {
      background: transparent;
      color: #ef4444;
      border: 1.5px solid rgba(239, 68, 68, 0.3);
    }
    .btn-danger:not(:disabled):hover {
      background: rgba(239, 68, 68, 0.1);
      border-color: #ef4444;
    }

    /* Dialog */
    .dialog-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    .dialog-box {
      background: var(--bg-surface);
      border-radius: var(--radius-lg);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      min-width: 360px;
      max-width: 480px;
      overflow: hidden;
    }
    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px;
      border-bottom: 1px solid var(--border-subtle);
    }
    .dialog-title {
      font-weight: 600;
      font-size: 15px;
      color: var(--text-primary);
    }
    .dialog-close-btn {
      background: none;
      border: none;
      color: var(--text-muted);
      cursor: pointer;
      padding: 4px;
      border-radius: var(--radius-sm);
    }
    .dialog-close-btn:hover {
      color: var(--text-primary);
      background: var(--bg-muted);
    }
    .dialog-body {
      padding: 20px;
      color: var(--text-secondary);
      font-size: 14px;
      line-height: 1.5;
    }
    .dialog-footer {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding: 12px 20px;
      border-top: 1px solid var(--border-subtle);
    }

    /* Meta bar */
    .editor__meta {
      display: flex;
      align-items: center;
      gap: 0;
      padding: 0 28px;
      background: var(--bg-surface);
      border-bottom: 1px solid var(--border-subtle);
      min-height: 44px;
    }
    .editor__meta--reason {
      background: var(--accent-subtle);
      border-bottom: 1px solid var(--accent-muted);
    }
    .meta-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      border-right: 1px solid var(--border-subtle);
      font-size: 0.8rem;
    }
    .meta-item:first-child { padding-left: 0; }
    .meta-item:last-child { border-right: none; }
    .meta-item__label {
      color: var(--text-tertiary);
      font-weight: 500;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      white-space: nowrap;
    }
    .meta-item__value {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* Meta select */
    .meta-select {
      appearance: none;
      -webkit-appearance: none;
      background: transparent;
      border: none;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--text-primary);
      font-family: inherit;
      cursor: pointer;
      padding: 2px 18px 2px 0;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23a1a1aa' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0 center;
      outline: none;
    }
    .meta-select:focus { color: var(--accent); }

    /* Phase pills in meta bar */
    .meta-phase-pill {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 10px;
      border-radius: var(--radius-full);
      font-size: 0.75rem;
      font-weight: 600;
      cursor: pointer;
      transition: all var(--duration) var(--ease);
      user-select: none;
      border: 1.5px solid transparent;
    }
    .meta-phase-pill:hover { filter: brightness(0.95); }
    .meta-phase-pill.active { border-color: currentColor; }
    .meta-phase-pill .check {
      width: 0;
      height: 12px;
      overflow: hidden;
      transition: width var(--duration) var(--ease);
    }
    .meta-phase-pill.active .check { width: 12px; }
    .meta-phase-pill.requirements { background: var(--phase-purple-bg); color: var(--phase-purple-text); }
    .meta-phase-pill.tpm          { background: var(--phase-blue-bg);   color: var(--phase-blue-text); }
    .meta-phase-pill.data         { background: var(--phase-teal-bg);   color: var(--phase-teal-text); }
    .meta-phase-pill.architect    { background: var(--phase-amber-bg);  color: var(--phase-amber-text); }
    .meta-phase-pill.coder        { background: var(--phase-rose-bg);   color: var(--phase-rose-text); }

    /* Priority bars */
    .meta-priority {
      display: flex;
      align-items: center;
      gap: 3px;
    }
    .meta-priority__bar {
      width: 4px;
      height: 14px;
      border-radius: 2px;
      background: var(--border-subtle);
      transition: background var(--duration) var(--ease);
      cursor: pointer;
    }
    .meta-priority__bar.filled { background: var(--accent); }
    .meta-priority__bar:hover { background: var(--accent-muted); }

    /* Status change reason input */
    .meta-reason-input {
      flex: 1;
      width: 100%;
      border: 1.5px solid var(--accent-muted);
      border-radius: var(--radius-sm);
      padding: 5px 10px;
      font-size: 0.8rem;
      font-family: inherit;
      color: var(--text-primary);
      background: var(--bg-surface);
      outline: none;
      transition: border-color var(--duration) var(--ease);
    }
    .meta-reason-input::placeholder { color: var(--text-tertiary); }
    .meta-reason-input:focus {
      border-color: var(--accent);
      box-shadow: 0 0 0 2px var(--accent-subtle);
    }

    /* Create banner */
    .create-banner {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 28px;
      background: var(--accent-subtle);
      border-bottom: 2px solid var(--accent-muted);
    }
    .create-banner__icon {
      width: 28px;
      height: 28px;
      border-radius: var(--radius-sm);
      background: var(--accent);
      color: var(--text-inverse);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .create-banner__icon svg { width: 14px; height: 14px; }
    .create-banner__text { flex: 1; }
    .create-banner__title {
      font-size: 0.85rem;
      font-weight: 700;
      color: var(--accent-hover);
      letter-spacing: -0.01em;
    }
    .create-banner__hint { font-size: 0.73rem; color: var(--text-tertiary); }
    .create-banner__actions {
      display: flex;
      gap: 8px;
      flex-shrink: 0;
    }

    /* Editor body */
    .editor__body {
      flex: 1;
      overflow: hidden;
      display: flex;
      justify-content: center;
      padding: 0;
    }
    .editor__writing-area {
      width: 100%;
      max-width: 820px;
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .editor__textarea {
      flex: 1;
      width: 100%;
      border: none;
      outline: none;
      resize: none;
      padding: 32px 40px;
      font-family: 'JetBrains Mono', 'SF Mono', 'Fira Code', monospace;
      font-size: 0.88rem;
      line-height: 1.8;
      color: var(--text-primary);
      background: transparent;
      tab-size: 2;
      box-sizing: border-box;
    }
    .editor__textarea::placeholder {
      color: var(--text-tertiary);
      font-style: italic;
    }

    /* Markdown preview */
    .editor__preview {
      flex: 1;
      width: 100%;
      padding: 32px 40px;
      overflow-y: auto;
      color: var(--text-primary);
      font-size: 0.92rem;
      line-height: 1.75;
    }
    .editor__preview h1 {
      font-size: 1.75rem;
      font-weight: 700;
      letter-spacing: -0.03em;
      margin: 0 0 0.5em;
    }
    .editor__preview h2 {
      font-size: 1.35rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      margin: 1.5em 0 0.4em;
    }
    .editor__preview h3 {
      font-size: 1.1rem;
      font-weight: 600;
      margin: 1.2em 0 0.3em;
    }
    .editor__preview p { margin: 0 0 1em; }
    .editor__preview ul, .editor__preview ol {
      margin: 0 0 1em;
      padding-left: 1.5em;
    }
    .editor__preview li { margin-bottom: 0.3em; }
    .editor__preview code {
      background: var(--bg-muted);
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'JetBrains Mono', monospace;
      font-size: 0.85em;
      color: var(--accent-hover);
    }
    .editor__preview pre {
      background: #1e1e2e;
      color: #cdd6f4;
      padding: 20px;
      border-radius: var(--radius-md);
      overflow-x: auto;
      margin: 0 0 1em;
      font-size: 0.84rem;
      line-height: 1.6;
    }
    .editor__preview pre code {
      background: none;
      padding: 0;
      color: inherit;
      font-size: inherit;
    }
    .editor__preview blockquote {
      border-left: 3px solid var(--accent);
      margin: 0 0 1em;
      padding: 8px 20px;
      color: var(--text-secondary);
      background: var(--accent-subtle);
      border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    }
    .editor__preview a { color: var(--accent); text-decoration: none; font-weight: 500; }
    .editor__preview a:hover { text-decoration: underline; }
    .editor__preview hr {
      border: none;
      border-top: 1px solid var(--border-subtle);
      margin: 2em 0;
    }
    .editor__preview table {
      width: 100%;
      border-collapse: collapse;
      margin: 0 0 1em;
      font-size: 0.88rem;
    }
    .editor__preview th, .editor__preview td {
      border: 1px solid var(--border-subtle);
      padding: 8px 14px;
      text-align: left;
    }
    .editor__preview th {
      background: var(--bg-muted);
      font-weight: 600;
    }
    .editor__preview strong { font-weight: 600; color: var(--text-primary); }

    /* Footer */
    .editor__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 28px;
      background: var(--bg-surface);
      border-top: 1px solid var(--border-subtle);
      font-size: 0.73rem;
      color: var(--text-tertiary);
    }
    .editor__footer-left {
      display: flex;
      gap: 16px;
    }
    .editor__footer span {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .editor__footer svg { width: 12px; height: 12px; }

    /* Empty state */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      gap: 16px;
      background: var(--bg-app);
    }
    .empty-state__illustration {
      width: 120px;
      height: 120px;
      background: var(--accent-subtle);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
    }
    .empty-state__illustration svg {
      width: 52px;
      height: 52px;
      color: var(--accent);
      opacity: 0.6;
    }
    .empty-state__title {
      font-size: 1.15rem;
      font-weight: 600;
      color: var(--text-primary);
      letter-spacing: -0.01em;
    }
    .empty-state__desc {
      font-size: 0.88rem;
      color: var(--text-tertiary);
      text-align: center;
      max-width: 280px;
    }

    /* Scrollbar for editor body */
    .editor__body { overflow-y: auto; }
    .editor__body::-webkit-scrollbar { width: 6px; }
    .editor__body::-webkit-scrollbar-track { background: transparent; }
    .editor__body::-webkit-scrollbar-thumb {
      background: var(--border-default);
      border-radius: var(--radius-full);
    }
  `]
})
@RegisterClass(BaseResourceComponent, 'NotesResource')
export class NotesResourceComponent extends BCBaseComponent implements OnInit, OnDestroy {
  Notes: NoteItem[] = [];
  FilteredNotes: NoteItem[] = [];
  SelectedNote: NoteItem | null = null;
  SearchTerm = '';
  StatusFilter = 'All';
  Loading = false;
  Saving = false;
  Dirty = false;
  CreateMode = false;
  CreatingNote = false;
  Deleting = false;
  PreviewMode = false;
  RenderedMarkdown = '';
  Organizations: { ID: string; Name: string }[] = [];
  NewNote = { Phase: '', Source: 'Manual', Status: 'Pending', Priority: 0, Note: '', OrganizationID: '' };
  StatusChangeReason: string | null = null;
  IsAdminUser = false;

  // Dialog state
  DialogVisible = false;
  DialogTitle = '';
  DialogMessage = '';
  DialogType: 'alert' | 'confirm' = 'alert';
  private dialogResolve: ((value: boolean) => void) | null = null;

  readonly PhaseMap: PhaseInfo[] = [
    { value: 'Requirements Expert', display: 'Requirements', cssClass: 'requirements' },
    { value: 'Technical Project Manager', display: 'TPM', cssClass: 'tpm' },
    { value: 'Data Expert', display: 'Data', cssClass: 'data' },
    { value: 'Software Architect', display: 'Architect', cssClass: 'architect' },
    { value: 'Coder', display: 'Coder', cssClass: 'coder' }
  ];

  readonly PriorityBars = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  private originalNote: NoteItem | null = null;
  CurrentOrgID = '';
  private cdr = inject(ChangeDetectorRef);

  @HostListener('document:keydown', ['$event'])
  HandleKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && this.CreateMode) {
      event.preventDefault();
      this.CreateNote();
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 's' && !this.CreateMode && this.SelectedNote && this.Dirty) {
      event.preventDefault();
      this.SaveNote();
    }
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit(); // BCBaseComponent handles tenant context loading + subscription
    await this.loadTenantInfo();
    await this.loadNotes();
    this.NotifyLoadComplete();
  }

  protected override async onContextChanged(ctx: BCTenantContext | null): Promise<void> {
    if (ctx) {
      await this.loadTenantInfo();
      await this.loadNotes();
      this.cdr.detectChanges();
    }
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ========== Dialog Helpers ==========

  private showAlert(title: string, message: string): void {
    this.DialogTitle = title;
    this.DialogMessage = message;
    this.DialogType = 'alert';
    this.DialogVisible = true;
    this.cdr.detectChanges();
  }

  private showConfirm(title: string, message: string): Promise<boolean> {
    this.DialogTitle = title;
    this.DialogMessage = message;
    this.DialogType = 'confirm';
    this.DialogVisible = true;
    this.cdr.detectChanges();
    return new Promise<boolean>(resolve => {
      this.dialogResolve = resolve;
    });
  }

  OnDialogClose(result: boolean): void {
    this.DialogVisible = false;
    if (this.dialogResolve) {
      this.dialogResolve(result);
      this.dialogResolve = null;
    }
    this.cdr.detectChanges();
  }

  private async loadTenantInfo(): Promise<void> {
    const ctx = this.tenantContextService.currentContext;
    if (ctx) {
      this.CurrentOrgID = ctx.organizationID;
      this.IsAdminUser = await this.isAuthorizedAsync('BCSaaS.Users');
    }

    const memberships = this.tenantContextService.GetAllMemberships();
    this.Organizations = memberships.map(m => ({ ID: m.organizationID, Name: m.organizationName }));
  }

  private async loadNotes(): Promise<void> {
    this.Loading = true;
    this.cdr.detectChanges();

    try {
      const rv = new RunView();
      const result = await rv.RunView({
        EntityName: 'Notes',
        ExtraFilter: `OrganizationID = '${this.CurrentOrgID}'`,
        OrderBy: '__mj_UpdatedAt DESC'
      });

      if (result.Success) {
        this.Notes = result.Results.map((r: any) => ({
          ID: r.ID,
          OrganizationID: r.OrganizationID || '',
          Phase: r.Phase || '',
          Source: r.Source || '',
          Status: r.Status || '',
          Priority: r.Priority || 0,
          Note: r.Note || '',
          UpdatedAt: r.__mj_UpdatedAt ? new Date(r.__mj_UpdatedAt) : null,
          CreatedAt: r.__mj_CreatedAt ? new Date(r.__mj_CreatedAt) : null
        }));
        this.FilterNotes();
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      this.Loading = false;
      this.cdr.detectChanges();
    }
  }

  FilterNotes(): void {
    let result = [...this.Notes];

    if (this.StatusFilter !== 'All') {
      result = result.filter(n => n.Status === this.StatusFilter);
    }

    if (this.SearchTerm.trim()) {
      const term = this.SearchTerm.toLowerCase();
      result = result.filter(n =>
        n.Note.toLowerCase().includes(term) ||
        n.Phase.toLowerCase().includes(term) ||
        n.Source.toLowerCase().includes(term)
      );
    }

    this.FilteredNotes = result;
  }

  async OnOrgFilterChange(orgID: string): Promise<void> {
    if (this.Dirty && this.SelectedNote) {
      if (!await this.showConfirm('Unsaved Changes', 'You have unsaved changes. Discard them?')) return;
    }
    this.CurrentOrgID = orgID;
    this.SelectedNote = null;
    this.originalNote = null;
    this.Dirty = false;
    this.CreateMode = false;

    // Update tenant context so server-side RLS matches the selected org
    this.tenantContextService.SetCurrentOrganization(orgID);
  }

  SetStatusFilter(filter: string): void {
    this.StatusFilter = filter;
    this.FilterNotes();
  }

  async SelectNote(note: NoteItem): Promise<void> {
    if (this.CreateMode) {
      if (this.NewNote.Note.trim() && !await this.showConfirm('Unsaved Draft', 'You have an unsaved draft. Discard it?')) return;
      this.CreateMode = false;
    }
    if (this.Dirty && this.SelectedNote) {
      if (!await this.showConfirm('Unsaved Changes', 'You have unsaved changes. Discard them?')) return;
    }
    this.SelectedNote = { ...note };
    this.originalNote = { ...note };
    this.Dirty = false;
    this.PreviewMode = false;
    this.StatusChangeReason = null;
  }

  MarkDirty(): void {
    this.Dirty = true;
  }

  OnStatusChange(): void {
    if (this.SelectedNote && this.originalNote && this.SelectedNote.Status !== this.originalNote.Status) {
      this.StatusChangeReason = '';
    } else {
      this.StatusChangeReason = null;
    }
    this.MarkDirty();
  }

  GetPhaseList(phase: string): string[] {
    if (!phase) return [];
    return phase.split(',').map(p => p.trim()).filter(p => p.length > 0);
  }

  GetPhaseDisplayName(fullName: string): string {
    const found = this.PhaseMap.find(p => p.value === fullName);
    return found ? found.display : fullName;
  }

  GetPhaseCssClass(fullName: string): string {
    const found = this.PhaseMap.find(p => p.value === fullName);
    return found ? found.cssClass : '';
  }

  IsPhaseSelected(phase: string): boolean {
    if (!this.SelectedNote) return false;
    return this.GetPhaseList(this.SelectedNote.Phase).includes(phase);
  }

  TogglePhase(phase: string): void {
    if (!this.SelectedNote) return;
    const phases = this.GetPhaseList(this.SelectedNote.Phase);
    const idx = phases.indexOf(phase);
    if (idx >= 0) {
      phases.splice(idx, 1);
    } else {
      phases.push(phase);
    }
    this.SelectedNote.Phase = phases.join(', ');
    this.MarkDirty();
  }

  IsNewNotePhaseSelected(phase: string): boolean {
    return this.GetPhaseList(this.NewNote.Phase).includes(phase);
  }

  ToggleNewNotePhase(phase: string): void {
    const phases = this.GetPhaseList(this.NewNote.Phase);
    const idx = phases.indexOf(phase);
    if (idx >= 0) {
      phases.splice(idx, 1);
    } else {
      phases.push(phase);
    }
    this.NewNote.Phase = phases.join(', ');
  }

  SetPriority(bar: number): void {
    if (!this.SelectedNote) return;
    const newVal = 10 - bar;
    this.SelectedNote.Priority = this.SelectedNote.Priority === newVal ? newVal + 1 : newVal;
    this.MarkDirty();
  }

  SetNewNotePriority(bar: number): void {
    const newVal = 10 - bar;
    this.NewNote.Priority = this.NewNote.Priority === newVal ? newVal + 1 : newVal;
  }

  SetPreviewMode(preview: boolean): void {
    this.PreviewMode = preview;
    if (preview && this.SelectedNote) {
      this.RenderedMarkdown = marked.parse(this.SelectedNote.Note || '') as string;
    }
  }

  DiscardChanges(): void {
    if (this.originalNote) {
      this.SelectedNote = { ...this.originalNote };
      this.Dirty = false;
      this.StatusChangeReason = null;
    }
  }

  async SaveNote(): Promise<void> {
    if (!this.SelectedNote) return;
    this.Saving = true;
    this.cdr.detectChanges();

    try {
      const md = new Metadata();
      const entity = await md.GetEntityObject<any>('Notes');
      const loaded = await entity.Load(this.SelectedNote.ID);
      if (!loaded) throw new Error('Could not load note record');

      entity.Phase = this.SelectedNote.Phase;
      entity.Source = 'Manual'; // User-edited notes are always Manual
      entity.Status = this.SelectedNote.Status;
      entity.Priority = this.SelectedNote.Priority;
      entity.Note = this.SelectedNote.Note;
      entity.OrganizationID = this.SelectedNote.OrganizationID || null;

      if (this.originalNote && this.SelectedNote.Status !== this.originalNote.Status) {
        entity.LatestStatusChangeDate = new Date().toISOString();
        entity.LatestStatusChangeReason = this.StatusChangeReason || null;
      }

      const saved = await entity.Save();
      if (!saved) throw new Error('Failed to save note');

      this.Dirty = false;
      this.StatusChangeReason = null;
      this.originalNote = { ...this.SelectedNote };

      const idx = this.Notes.findIndex(n => n.ID === this.SelectedNote!.ID);
      if (idx >= 0) {
        this.Notes[idx] = { ...this.SelectedNote, UpdatedAt: new Date() };
        this.SelectedNote.UpdatedAt = new Date();
        this.FilterNotes();
      }
    } catch (error) {
      console.error('Failed to save note:', error);
      this.showAlert('Save Failed', 'Failed to save note. See console for details.');
    } finally {
      this.Saving = false;
      this.cdr.detectChanges();
    }
  }

  async DeleteNote(): Promise<void> {
    if (!this.SelectedNote) return;
    if (!await this.showConfirm('Delete Note', 'Are you sure you want to delete this note? This cannot be undone.')) return;

    this.Deleting = true;
    this.cdr.detectChanges();

    try {
      const md = new Metadata();
      const entity = await md.GetEntityObject<any>('Notes');
      const loaded = await entity.Load(this.SelectedNote.ID);
      if (!loaded) throw new Error('Could not load note record');

      const deleted = await entity.Delete();
      if (!deleted) throw new Error('Failed to delete note');

      this.Notes = this.Notes.filter(n => n.ID !== this.SelectedNote!.ID);
      this.SelectedNote = null;
      this.originalNote = null;
      this.Dirty = false;
      this.FilterNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      this.showAlert('Delete Failed', 'Failed to delete note. See console for details.');
    } finally {
      this.Deleting = false;
      this.cdr.detectChanges();
    }
  }

  GetPreview(note: string): string {
    const plain = note.replace(/^#{1,6}\s+/gm, '').replace(/[*_`~|]/g, '').replace(/\n/g, ' ');
    return plain.substring(0, 120);
  }

  async EnterCreateMode(): Promise<void> {
    if (this.Dirty && this.SelectedNote) {
      if (!await this.showConfirm('Unsaved Changes', 'You have unsaved changes. Discard them?')) return;
    }
    this.NewNote = { Phase: '', Source: 'Manual', Status: 'Pending', Priority: 0, Note: '', OrganizationID: this.CurrentOrgID };
    this.CreateMode = true;
    this.SelectedNote = null;
    this.originalNote = null;
    this.Dirty = false;
    this.PreviewMode = false;
  }

  CancelCreate(): void {
    this.CreateMode = false;
  }

  async CreateNote(): Promise<void> {
    this.CreatingNote = true;
    this.cdr.detectChanges();

    try {
      const md = new Metadata();
      const entity = await md.GetEntityObject<any>('Notes');
      entity.NewRecord();

      entity.Phase = this.NewNote.Phase;
      entity.Source = this.NewNote.Source;
      entity.Status = this.NewNote.Status;
      entity.Priority = this.NewNote.Priority;
      entity.Note = this.NewNote.Note;
      entity.OrganizationID = this.NewNote.OrganizationID || null;

      const saved = await entity.Save();
      if (!saved) throw new Error('Failed to create note');

      this.CreateMode = false;
      await this.loadNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
      this.showAlert('Create Failed', 'Failed to create note. See console for details.');
    } finally {
      this.CreatingNote = false;
      this.cdr.detectChanges();
    }
  }

  async GetResourceDisplayName(): Promise<string> {
    return 'Notes';
  }

  async GetResourceIconClass(): Promise<string> {
    return 'fa-solid fa-note-sticky';
  }
}
